import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useApi } from '../../../hooks/useApi';
import { getUsageRightRequests } from '../../../services/usageRights.service';
import { UsageRightContextType, UsageRightRequest, UsageRightStatus } from './types';

interface Props {
  children?: React.ReactNode;
}

const UsageRightsContext = React.createContext<UsageRightContextType>({
  currentData: [],
  currentPage: 1,
  isLoadingUsageRights: false,
  status: null,
  changePage: () => {},
  changeStatus: () => {},
  selectedRequest: null,
  setSelectedRequest: () => {},
  clearAndRefetch: () => {},
  pageCount: 0,
});

export const PageSize = 5;

function UsageRightsProvider({ children }: Props) {
  const { state } = useLocation();

  const [currentPage, setCurrentPage] = useState(1);
  const [status, setStatus] = useState<UsageRightStatus | null>(null);
  const [requests, setRequests] = useState<Record<string, UsageRightRequest[]>>({});
  const [selectedRequest, setSelectedRequest] = useState<UsageRightRequest | null>(() => {
    if (state?.usageRightData) return state.usageRightData;
    return null;
  });
  const [totalCount, setTotalCount] = useState<Record<string, number>>({});

  useEffect(() => {
    if (state?.usageRightRequest) setSelectedRequest(state.usageRightRequest);
  }, [state]);

  const { isLoading, triggerFetch } = useApi(getUsageRightRequests, {
    onSuccess: (data) => {
      const key = `${status}-${currentPage}`;
      setRequests((existingRequests) => ({ ...existingRequests, [key]: data?.data?.usageRequests || [] }));
      setTotalCount((existingCount) => ({ ...existingCount, [key]: data?.data?.totalPages }));
    },
    args: {
      page: currentPage,
      status,
      pageSize: PageSize,
    },
  });

  const clearAndRefetch = useCallback(async () => {
    setCurrentPage(1);
    setStatus(null);
    const data = await triggerFetch({
      args: {
        page: 1,
        pageSize: PageSize,
      },
      returnOnly: true,
    });
    const key = `null-1`;
    setRequests({ [key]: data?.data?.usageRequests || [] });
    setTotalCount((existingCount) => ({ ...existingCount, [key]: data?.data?.totalPages }));
  }, [setRequests, triggerFetch, setCurrentPage, setStatus]);

  const changeStatus = useCallback(
    async (newStatus: UsageRightStatus) => {
      setStatus(newStatus);
      const key = `${newStatus}-${currentPage}`;
      const existingData = requests[key];
      if (existingData === undefined) {
        const data = await triggerFetch({
          args: {
            page: currentPage,
            status: newStatus,
            pageSize: PageSize,
          },
          returnOnly: true,
        });
        setRequests((existingRequests) => ({ ...existingRequests, [key]: data?.data?.usageRequests || [] }));
        setTotalCount((existingCount) => ({ ...existingCount, [key]: data?.data?.totalPages }));
      }
    },
    [setStatus, currentPage, triggerFetch, requests]
  );

  const changePage = useCallback(
    async (newPage: number) => {
      setCurrentPage(newPage);
      const key = `${status}-${newPage}`;
      const existingData = requests[key];
      if (existingData === undefined) {
        const data = await triggerFetch({
          args: {
            page: newPage,
            status,
            pageSize: PageSize,
          },
          returnOnly: true,
        });
        setRequests((existingRequests) => ({ ...existingRequests, [key]: data?.data?.usageRequests || [] }));
        setTotalCount((existingCount) => ({ ...existingCount, [key]: data?.data?.totalPages }));
      }
    },
    [setCurrentPage, status, triggerFetch, requests]
  );

  const contextData = useMemo(
    () => ({
      currentData: requests[`${status}-${currentPage}`],
      isLoadingUsageRights: isLoading,
      status,
      changeStatus,
      changePage,
      currentPage,
      selectedRequest,
      setSelectedRequest,
      clearAndRefetch,
      pageCount: totalCount[`${status}-${currentPage}`],
    }),
    [
      isLoading,
      status,
      currentPage,
      requests,
      changeStatus,
      changePage,
      selectedRequest,
      setSelectedRequest,
      clearAndRefetch,
      totalCount,
    ]
  );

  return <UsageRightsContext.Provider value={contextData}>{children}</UsageRightsContext.Provider>;
}

export const useUsageRight = () => {
  return useContext(UsageRightsContext);
};

export default UsageRightsProvider;
